import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

interface UseSearchOptions<T> {
  data: T[];
  searchableKeys?: (keyof T & string)[];
  debounceMs?: number;
}

interface UseSearchReturn<T> {
  searchTerm: string;
  debouncedSearchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredData: T[];
  clearSearch: () => void;
  matchCount: number;
  isSearching: boolean;
}

// Custom debounce hook
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useSearch<T extends Record<string, unknown>>({
  data,
  searchableKeys,
  debounceMs = 300
}: UseSearchOptions<T>): UseSearchReturn<T> {
  const [searchTerm, setSearchTermState] = useState('');
  const dataVersionRef = useRef(0);

  // Track data version to handle race conditions
  useEffect(() => {
    dataVersionRef.current += 1;
  }, [data]);

  // Debounce the search term
  const debouncedSearchTerm = useDebouncedValue(searchTerm, debounceMs);

  // Check if we're currently waiting for debounce
  const isSearching = searchTerm !== debouncedSearchTerm;

  // Memoized search with debounced term
  const filteredData = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return data;
    }

    const lowerSearchTerm = debouncedSearchTerm.toLowerCase().trim();
    const searchWords = lowerSearchTerm.split(/\s+/).filter(Boolean);

    return data.filter(row => {
      // If searchableKeys specified, only search those keys
      const keysToSearch = searchableKeys || (Object.keys(row) as (keyof T & string)[]);

      // Check if all search words match at least one field
      return searchWords.every(word =>
        keysToSearch.some(key => {
          const value = row[key];
          if (value === null || value === undefined) {
            return false;
          }
          return String(value).toLowerCase().includes(word);
        })
      );
    });
  }, [data, debouncedSearchTerm, searchableKeys]);

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTermState('');
  }, []);

  return {
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
    filteredData,
    clearSearch,
    matchCount: filteredData.length,
    isSearching
  };
}
